import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  MenuItem,
  TextField,
} from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import TimelineIcon from '@mui/icons-material/Timeline';
import { getReportMetrics } from '../../services/reportService';
import { ReportTrendChartSkeleton } from './PageSkeletons';

const chartTheme = {
  axis: {
    ticks: { text: { fill: '#666', fontSize: 11 } },
    legend: { text: { fill: '#240B4B', fontSize: 12, fontWeight: 600 } },
  },
  grid: { line: { stroke: '#eee' } },
  tooltip: {
    container: { fontSize: 12, borderRadius: 6 },
  },
};

const lineProps = {
  margin: { top: 16, right: 24, bottom: 48, left: 52 },
  xScale: { type: 'time', format: 'native', precision: 'day' },
  xFormat: 'time:%d.%m.%Y',
  yScale: { type: 'linear', min: 'auto', stacked: false },
  axisBottom: {
    format: '%d.%m',
    tickRotation: -35,
    legend: 'Tarih',
    legendOffset: 40,
  },
  axisLeft: { legendOffset: -44 },
  pointSize: 6,
  pointBorderWidth: 2,
  useMesh: true,
  enableArea: false,
  theme: chartTheme,
};

/**
 * CR + significance trend charts from metric snapshots
 */
const ReportTrendChart = ({ reportId, defaultMetric }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(defaultMetric || '');

  useEffect(() => {
    if (defaultMetric) setSelectedMetric(defaultMetric);
  }, [defaultMetric]);

  useEffect(() => {
    if (!reportId) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getReportMetrics(reportId, selectedMetric || undefined);
        setData(result);
        if (!selectedMetric && result.activeMetric) {
          setSelectedMetric(result.activeMetric);
        }
      } catch {
        setError('Trend verileri yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [reportId, selectedMetric]);

  if (loading) {
    return <ReportTrendChartSkeleton />;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 3, textAlign: 'center' }}>
        {error}
      </Typography>
    );
  }

  if (!data?.hasHistory) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          border: '1px dashed #ccc',
          borderRadius: 2,
          bgcolor: '#fafafa',
        }}
      >
        <TimelineIcon sx={{ fontSize: 48, color: '#bbb', mb: 1 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#240B4B', mb: 1 }}>
          Henüz trend verisi yok
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Extension ile raporu periyodik kaydettiğinizde CR ve significance eğrileri burada görünür.
          {data?.snapshotCount === 1 && ' Şu an yalnızca 1 kayıt var — en az 2 kayıt gerekli.'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {data.metricNames?.length > 1 && (
        <TextField
          select
          size="small"
          label="Metrik"
          value={selectedMetric}
          onChange={(e) => setSelectedMetric(e.target.value)}
          sx={{ maxWidth: 280 }}
        >
          {data.metricNames.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Paper elevation={0} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#240B4B', mb: 1 }}>
          Conversion Rate (CR) Trendi
        </Typography>
        <Box sx={{ height: 280 }}>
          <ResponsiveLine
            data={data.conversionRateSeries}
            {...lineProps}
            axisLeft={{ ...lineProps.axisLeft, legend: 'CR (%)' }}
            colors={(series) => series.color}
            tooltip={({ point }) => (
              <Box sx={{ p: 1, bgcolor: '#fff', border: '1px solid #eee', borderRadius: 1 }}>
                <strong>{point.seriesId}</strong>
                <br />
                {point.data.xFormatted}: {Number(point.data.y).toFixed(2)}%
              </Box>
            )}
          />
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#240B4B', mb: 1 }}>
          Significance Trendi
        </Typography>
        <Box sx={{ height: 240 }}>
          <ResponsiveLine
            data={data.significanceSeries}
            {...lineProps}
            axisLeft={{ ...lineProps.axisLeft, legend: 'Olasılık (%)' }}
            colors={(series) => series.color}
            tooltip={({ point }) => (
              <Box sx={{ p: 1, bgcolor: '#fff', border: '1px solid #eee', borderRadius: 1 }}>
                <strong>{point.seriesId}</strong>
                <br />
                {point.data.xFormatted}: {Number(point.data.y).toFixed(1)}%
              </Box>
            )}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportTrendChart;
